import { useEffect, useRef, useState } from 'react'
import { useParams } from 'react-router-dom'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useCurrentUser, useLogout } from '../../features/auth/hooks'
import { uploadCurrentUserAvatar } from '../../features/auth/api'
import { useClient } from '../../features/clients/hooks'
import { Modal } from '../ui/Modal'
import { Input } from '../ui/Input'
import { Button } from '../ui/Button'
import { ChangePasswordModal } from './ChangePasswordModal'

export function UserMenu() {
  const { data: user } = useCurrentUser()
  const { clientId } = useParams<{ clientId: string }>()
  const { data: client } = useClient(clientId ?? null)
  const logout = useLogout()
  const fileInputRef = useRef<HTMLInputElement>(null)
  const menuRef = useRef<HTMLDivElement>(null)
  const [avatarFailed, setAvatarFailed] = useState(false)
  const [menuOpen, setMenuOpen] = useState(false)
  const [editProfileOpen, setEditProfileOpen] = useState(false)
  const [changePasswordOpen, setChangePasswordOpen] = useState(false)
  const [noteOpen, setNoteOpen] = useState(false)
  const queryClient = useQueryClient()
  const uploadAvatar = useMutation({
    mutationFn: uploadCurrentUserAvatar,
    onSuccess: (updatedUser) => {
      queryClient.setQueryData(['users', 'me'], updatedUser)
      queryClient.invalidateQueries({ queryKey: ['users', 'me'] })
    },
  })

  useEffect(() => {
    setAvatarFailed(false)
  }, [user?.avatar_url])

  useEffect(() => {
    if (!menuOpen) return

    function onPointerDown(event: PointerEvent) {
      if (!menuRef.current?.contains(event.target as Node)) {
        setMenuOpen(false)
      }
    }

    function onKeyDown(event: KeyboardEvent) {
      if (event.key === 'Escape') setMenuOpen(false)
    }

    document.addEventListener('pointerdown', onPointerDown)
    document.addEventListener('keydown', onKeyDown)
    return () => {
      document.removeEventListener('pointerdown', onPointerDown)
      document.removeEventListener('keydown', onKeyDown)
    }
  }, [menuOpen])

  if (!user) return null

  const clientRole = client?.current_user_role
  const displayName = user.name ?? user.email
  const initials = displayName
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase())
    .join('') || user.email[0]?.toUpperCase() || '?'

  return (
    <div className="flex items-center gap-4">
      {/* Name + role: hidden on narrow shell */}
      <div className="hidden @md/shell:block text-right">
        <p className="font-sans font-540 text-body-sm text-black leading-tight">
          {displayName}
        </p>
        <div className="flex flex-col items-end gap-1">
          {clientRole && (
            <p className="font-sans font-450 text-[10px] uppercase tracking-[0.06em] text-black/45 leading-tight mt-0.5">
              {clientRole}
            </p>
          )}
        </div>
      </div>

      {/* Avatar button → triggers file pick */}
      <button
        type="button"
        aria-label="Upload profile photo"
        title="Upload profile photo"
        className="size-12 shrink-0 overflow-hidden rounded-full border border-black/[0.08] bg-black/[0.04] shadow-soft transition-transform hover:scale-[1.03] focus-visible:outline-dashed focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-violet disabled:cursor-not-allowed disabled:opacity-60"
        onClick={() => fileInputRef.current?.click()}
        disabled={uploadAvatar.isPending}
      >
        {user.avatar_url && !avatarFailed ? (
          <img
            src={user.avatar_url}
            alt={displayName}
            className="h-full w-full object-cover"
            onError={() => setAvatarFailed(true)}
          />
        ) : (
          <div className="flex h-full w-full items-center justify-center bg-gradient-to-r from-brand-blue via-brand-violet to-brand-pink font-450 text-body-sm text-white">
            {initials}
          </div>
        )}
      </button>
      <input
        ref={fileInputRef}
        type="file"
        accept="image/jpeg,image/png,image/webp"
        className="sr-only"
        onChange={(event) => {
          const file = event.target.files?.[0]
          if (file) uploadAvatar.mutate(file)
          event.target.value = ''
        }}
      />

      {/* 3-dot menu */}
      <div ref={menuRef} className="relative">
        <button
          type="button"
          aria-label="Open user menu"
          aria-haspopup="menu"
          aria-expanded={menuOpen}
          className="inline-flex size-10 items-center justify-center rounded-full text-black transition-colors hover:bg-black/[0.04] focus-visible:outline-dashed focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-violet"
          onClick={() => setMenuOpen((open) => !open)}
        >
          <span aria-hidden className="flex flex-col items-center gap-1">
            <span className="size-1 rounded-full bg-current" />
            <span className="size-1 rounded-full bg-current" />
            <span className="size-1 rounded-full bg-current" />
          </span>
        </button>

        {menuOpen && (
          <div
            role="menu"
            className="absolute right-0 top-12 z-50 w-60 overflow-hidden rounded-xl border border-black/[0.08] bg-white py-1.5 text-left shadow-soft-lg"
          >
            {/* Profile group */}
            <div className="px-3 py-2 border-b border-black/[0.06] mb-1">
              <p className="font-540 text-body-sm text-black leading-tight truncate">{displayName}</p>
              <p className="text-[11px] text-black/45 truncate mt-0.5">{user.email}</p>
            </div>

            <MenuItem
              label="แก้ไขข้อมูลส่วนตัว"
              onClick={() => { setMenuOpen(false); setEditProfileOpen(true) }}
            />
            <MenuItem
              label="แก้ไขรูปโปรไฟล์"
              onClick={() => { setMenuOpen(false); fileInputRef.current?.click() }}
            />
            <MenuItem
              label="เปลี่ยนรหัสผ่าน"
              onClick={() => { setMenuOpen(false); setChangePasswordOpen(true) }}
            />

            <div className="my-1 border-t border-black/[0.06]" />

            <MenuItem
              label="ส่ง note ถึงทีมงาน"
              onClick={() => { setMenuOpen(false); setNoteOpen(true) }}
            />
            <MenuItem
              label="Sign out"
              onClick={() => { setMenuOpen(false); logout.mutate() }}
              disabled={logout.isPending}
              danger
            />
          </div>
        )}
      </div>

      {/* Edit profile modal */}
      <EditProfileModal
        open={editProfileOpen}
        onClose={() => setEditProfileOpen(false)}
        name={displayName}
        email={user.email}
      />

      {/* Change password modal */}
      {changePasswordOpen && (
        <ChangePasswordModal onClose={() => setChangePasswordOpen(false)} />
      )}

      {/* Note to team modal */}
      <NoteToTeamModal
        open={noteOpen}
        onClose={() => setNoteOpen(false)}
        senderName={displayName}
      />
    </div>
  )
}

// ─── Helpers ──────────────────────────────────────────────────────────

function MenuItem({
  label,
  onClick,
  disabled,
  danger,
}: {
  label: string
  onClick: () => void
  disabled?: boolean
  danger?: boolean
}) {
  return (
    <button
      type="button"
      role="menuitem"
      disabled={disabled}
      onClick={onClick}
      className={`block w-full px-4 py-2.5 text-left text-body-sm font-330 hover:bg-black/[0.04] disabled:opacity-50 ${danger ? 'text-rose-600' : 'text-black/80'}`}
    >
      {label}
    </button>
  )
}

// ─── Note to team modal ───────────────────────────────────────────────

const TEAM_MEMBERS = [
  { id: 'all',  label: 'ทีมทั้งหมด' },
  { id: 'mgmt', label: 'Management' },
  { id: 'mkt',  label: 'Marketing team' },
  { id: 'ops',  label: 'Operations' },
]

function NoteToTeamModal({
  open,
  onClose,
  senderName,
}: {
  open: boolean
  onClose: () => void
  senderName: string
}) {
  const [note, setNote] = useState('')
  const [to, setTo] = useState('all')
  const [sent, setSent] = useState(false)

  function handleSend() {
    if (!note.trim()) return
    setSent(true)
    setTimeout(() => {
      setSent(false)
      setNote('')
      setTo('all')
      onClose()
    }, 1200)
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="ส่ง note ถึงทีมงาน"
      size="md"
      footer={
        <>
          <Button variant="glass-dark" onClick={onClose}>ยกเลิก</Button>
          <Button onClick={handleSend} disabled={!note.trim() || sent}>
            {sent ? 'ส่งแล้ว ✓' : 'ส่ง note'}
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        <div className="flex flex-col gap-1.5">
          <span className="text-body-sm font-450 text-black/70">จาก</span>
          <div className="px-3 py-2 rounded-lg border border-black/[0.08] bg-black/[0.02] text-body-sm text-black/60 font-330">
            {senderName}
          </div>
        </div>
        <div className="flex flex-col gap-1.5">
          <span className="text-body-sm font-450 text-black/70">ถึง</span>
          <select
            value={to}
            onChange={(e) => setTo(e.target.value)}
            className="w-full rounded-lg border border-black/[0.08] bg-white px-3 py-2 text-body-sm font-330 outline-none focus:border-black/30"
          >
            {TEAM_MEMBERS.map((m) => (
              <option key={m.id} value={m.id}>{m.label}</option>
            ))}
          </select>
        </div>
        <div className="flex flex-col gap-1.5">
          <span className="text-body-sm font-450 text-black/70">ข้อความ</span>
          <textarea
            autoFocus
            rows={4}
            placeholder="เขียน note หรือ feedback ที่ต้องการส่งให้ทีม…"
            value={note}
            onChange={(e) => setNote(e.target.value)}
            className="w-full rounded-lg border border-black/[0.08] bg-white px-3 py-2 text-body-sm font-330 outline-none focus:border-black/30 resize-none placeholder:text-black/30"
          />
          <span className="text-[11px] text-black/35 text-right">{note.length} / 500</span>
        </div>
      </div>
    </Modal>
  )
}

// ─── Edit profile modal ───────────────────────────────────────────────

function EditProfileModal({
  open,
  onClose,
  name,
  email,
}: {
  open: boolean
  onClose: () => void
  name: string
  email: string
}) {
  const [displayName, setDisplayName] = useState(name)
  const [phone, setPhone] = useState('')
  const [saved, setSaved] = useState(false)

  const handleSave = () => {
    setSaved(true)
    setTimeout(() => { setSaved(false); onClose() }, 800)
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="แก้ไขข้อมูลส่วนตัว"
      size="md"
      footer={
        <>
          <Button variant="glass-dark" onClick={onClose}>ยกเลิก</Button>
          <Button onClick={handleSave}>{saved ? 'บันทึกแล้ว ✓' : 'บันทึก'}</Button>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        <Input label="ชื่อ-นามสกุล" value={displayName} onChange={(e) => setDisplayName(e.target.value)} />
        <Input label="อีเมล" value={email} disabled />
        <Input label="เบอร์โทรศัพท์" placeholder="0812345678" value={phone} onChange={(e) => setPhone(e.target.value)} />
      </div>
    </Modal>
  )
}
